// externals
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

// internals
import { recentDealsSelectors } from '../store';
import { parseStoredDealership } from '../utils/dealershipUtils';
import type { SelectedDealership } from '../types';

// Constants
import { RECENT_DEALS_SELECTED_DEALERSHIP_KEY } from '../constants';

/**
 * Hook that abstracts dealership info resolution.
 * Provides a single interface to get/set dealership info regardless of source.
 *
 * - getDealerInfo: Returns dealership from session storage (priority) or Redux store (fallback)
 * - setDealerInfo: Persists dealership to session storage
 */
export const useRecentDealsDealershipInfo = () => {
    const { getDealerInfo: getDealerInfoSelector } = recentDealsSelectors.additionalSelectors;
    const dealerInfo = useSelector(getDealerInfoSelector);

    /**
     * Resolves dealership info from session storage first, falls back to Redux store.
     */
    const getDealerInfo = useCallback((): SelectedDealership | undefined => {
        const storedDealership = parseStoredDealership(sessionStorage.getItem(RECENT_DEALS_SELECTED_DEALERSHIP_KEY));
        if (storedDealership) return storedDealership;

        const { mmdId, coOrgId } = dealerInfo || {};
        return mmdId ? { mmdId, coOrgId } : undefined;
    }, [dealerInfo]);

    /**
     * Persists dealership info to session storage.
     */
    const setDealerInfo = useCallback((dealership: SelectedDealership): void => {
        sessionStorage.setItem(RECENT_DEALS_SELECTED_DEALERSHIP_KEY, JSON.stringify(dealership));
    }, []);

    return { getDealerInfo, setDealerInfo };
};
